/**
 * i18n Namespace Configuration
 *
 * Defines which namespaces each route requires.
 * Routes/components use this to declare their dependencies.
 *
 * IMPORTANT: This is a STATIC mapping. The loader does NOT
 * interpret routes - components call ensureNamespaces() directly.
 *
 * @module i18n/namespaces
 */

/**
 * Route to Namespace Mapping
 *
 * Each route declares which namespaces it needs.
 * The loader will ensure these are loaded before rendering.
 *
 * INVARIANT: Namespaces must never overlap in key space.
 * Each namespace owns unique top-level prefixes:
 * - core.json: core.*, meta.*, home.*, legal.* (legal.notice)
 * - shared.json: shared.*
 * - memorama.json: memorama.*
 * - puzzle.json: puzzle.*
 * - static.json: about.*, contact.*, support.*, notFound.*
 * - legal.json: privacy.*, terms.* (only es/en have this file)
 */
export const ROUTE_NAMESPACES = {
  // Home page (core.json contains core.*, meta.*, home.*)
  home: ['core'],

  // Game pages
  memorama: ['core', 'shared', 'memorama'],
  puzzle: ['core', 'shared', 'puzzle'],

  // Static pages
  about: ['core', 'static'],
  contact: ['core', 'static'],
  support: ['core', 'static'],
  notFound: ['core', 'static'],

  // Legal pages (special handling)
  privacy: ['core', 'legal'],
  terms: ['core', 'legal']
}

/**
 * Namespace Fallbacks
 *
 * Defines fallback languages for specific namespaces.
 * Used when a namespace doesn't exist for a language.
 *
 * Currently only 'legal' has fallbacks:
 * - German (de) falls back to English (en)
 * - All other languages fall back to Spanish (es)
 */
export const NAMESPACE_FALLBACKS = {
  legal: {
    default: 'es',
    de: 'en'
  }
}

/**
 * Languages that have their own legal content
 * Used by components to determine whether to show legal.notice
 */
export const LANGUAGES_WITH_LEGAL = ['es', 'en']

/**
 * Namespaces loaded on app initialization
 * These are always available after first load
 */
export const INITIAL_NAMESPACES = ['core']

/**
 * All available namespaces
 * Useful for validation and debugging
 */
export const ALL_NAMESPACES = [
  'core',
  'shared',
  'memorama',
  'puzzle',
  'static',
  'legal'
]

/**
 * Fallback language for key-level fallback system
 *
 * When a translation key is missing in the current language,
 * this language's translations will be loaded and used.
 *
 * Currently always Spanish (ES) - the base/default language.
 * Future enhancement: could support language chains (nap→it→es)
 */
export const FALLBACK_LANGUAGE = 'es'

/**
 * Maps key prefixes to their source namespace files
 *
 * Used by the key-level fallback system to determine which
 * namespace to load when a key is missing.
 *
 * INVARIANT: Must stay in sync with actual namespace file contents
 *
 * File contents:
 * - core.json: core.*, meta.*, home.*, legal.* (legal.notice)
 * - shared.json: shared.*
 * - memorama.json: memorama.*
 * - puzzle.json: puzzle.*
 * - static.json: about.*, contact.*, support.*, notFound.*
 * - legal.json: privacy.*, terms.* (only es/en have this file)
 *
 * Note: legal.* keys (like legal.notice) are in core.json, not legal.json.
 * The legal.json file contains privacy.* and terms.* (actual legal text).
 */
export const NAMESPACE_PREFIXES = {
  core: ['core', 'meta', 'home', 'legal'],
  shared: ['shared'],
  memorama: ['memorama'],
  puzzle: ['puzzle'],
  static: ['about', 'contact', 'support', 'notFound'],
  legal: ['privacy', 'terms']
}

/**
 * Get namespaces required for a route
 *
 * @param {string} route - Route identifier (home, memorama, about, etc.)
 * @returns {string[]} Required namespaces
 *
 * @example
 * getRouteNamespaces('memorama') // ['core', 'shared', 'memorama']
 * getRouteNamespaces('unknown')  // ['core'] (fallback)
 */
export function getRouteNamespaces (route) {
  return ROUTE_NAMESPACES[route] || INITIAL_NAMESPACES
}

/**
 * Get fallback language for a namespace
 *
 * @param {string} namespace - Namespace name
 * @param {string} lang - Current language code
 * @returns {string|null} Fallback language code, or null if no fallback defined
 *
 * @example
 * getNamespaceFallback('legal', 'gl') // 'es'
 * getNamespaceFallback('legal', 'de') // 'en'
 * getNamespaceFallback('legal', 'es') // null (has own content)
 * getNamespaceFallback('core', 'gl')  // null (no fallback for core)
 */
export function getNamespaceFallback (namespace, lang) {
  const fallbackConfig = NAMESPACE_FALLBACKS[namespace]
  if (!fallbackConfig) return null

  // If this language has its own content, no fallback needed
  if (namespace === 'legal' && LANGUAGES_WITH_LEGAL.includes(lang)) {
    return null
  }

  return fallbackConfig[lang] || fallbackConfig.default || null
}

/**
 * Check if a namespace is valid
 *
 * @param {string} namespace - Namespace to validate
 * @returns {boolean}
 */
export function isValidNamespace (namespace) {
  return ALL_NAMESPACES.includes(namespace)
}

/**
 * Get namespace from a translation key
 *
 * Derives the source namespace file from a key's first segment.
 * Used by the fallback system to know which namespace to load.
 *
 * @param {string} key - Translation key (e.g., 'memorama.start.title')
 * @returns {string} Namespace name (e.g., 'memorama')
 *
 * @example
 * getNamespaceFromKey('core.navigation.home')  // 'core'
 * getNamespaceFromKey('memorama.start.title')  // 'memorama'
 * getNamespaceFromKey('privacy.title')         // 'legal'
 * getNamespaceFromKey('legal.notice')          // 'core' (legal.* is in core.json!)
 */
export function getNamespaceFromKey (key) {
  const prefix = key.split('.')[0]

  for (const [namespace, prefixes] of Object.entries(NAMESPACE_PREFIXES)) {
    if (prefixes.includes(prefix)) {
      return namespace
    }
  }

  // Unknown prefix - default to core and warn in development
  if (import.meta.env.MODE === 'development') {
    console.warn(`[i18n] Unknown key prefix "${prefix}" in key "${key}", defaulting to core namespace`)
  }

  return 'core'
}
